import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";

const ActivebuttonStyle={
  padding: "5px 15px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: "#ffffff",
  color: "#1f2937",
  boxShadow: "none",
}
const buttonStyle={
  padding: "6px 16px",
  border: "none",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: "transparent",
  color: "#6b7280"
}

function DashboardBudgetChart({ apiData }) {
  const [currentChart,setCurrentChart] =useState("expense")

  const handleChartchange =(val)=>{
      setCurrentChart(val)
  }

  const chartData = useMemo(() => {
    const data = currentChart === "income"
      ? apiData?.api4 || []
      : apiData?.api2 || [];
    
    // Ensure data is an array before calling .map()
    const safeData = Array.isArray(data) ? data : [];
    
    return {
      categories: safeData.map((item) => item.month),
      expected: safeData.map((item) => item.foreCastAmount),
      actual: safeData.map((item) => item.amount),
      percentages: safeData.map((item) => item.usagePercentage),
    };
  }, [apiData?.api2,apiData?.api4, currentChart]);

  const budgetChartOptions = useMemo(() => ({
    chart: {
      type: "bar",
      height: 350,
      toolbar: { show: false },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "70%",
        borderRadius: 4,
        dataLabels: { position: "top" },
        distributed: false,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (_, opts) {
        const percentage = chartData.percentages?.[opts.dataPointIndex];
        return opts.seriesIndex === 1 &&
          percentage !== null &&
          percentage !== undefined
          ? `${percentage}%`
          : "";
      },
      style: {
        fontSize: "11px",
        fontWeight: "600",
        colors: ["#6B7280"],
      },
      offsetY: -20,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: chartData.categories,   // ✅ now always in sync
      labels: {
        style: { colors: "#6B7280", fontSize: "11px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (val) =>
          val >= 1000
            ? `${(val / 1000).toFixed(0)},${String(val % 1000).padStart(3, "0")}`
            : val,
        style: { colors: "#6B7280", fontSize: "11px" },
      },
    },
    fill: { opacity: 1 },
    colors: [
      ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex === 0) return "#F8BBD9";
        const actual = chartData.actual[dataPointIndex];
        const expected = chartData.expected[dataPointIndex];
        return actual > expected ? "#6B5A85" : "#D8B4FE";  // ✅ correct color logic
      },
    ],
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      markers: { width: 10, height: 10, radius: 2 },
    },
    grid: {
      borderColor: "#F3F4F6",
      strokeDashArray: 3,
    },
    tooltip: {
      y: {
        formatter: (val) => `₹${val.toLocaleString()}`,
      },
    },
  }), [chartData]);  // ✅ recomputes when chartData changes

  const budgetChartSeries = useMemo(() => [
    { name: "Budget", data: chartData.expected },
    { name: "Actual", data: chartData.actual },
  ], [chartData]);

  return (
    <div style={{ backgroundColor: "#ffffff", width: "100%", padding: "24px 90px" }}>
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "none",
          padding: "24px",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1F2937", margin: "0 0 8px 0" }}>
            {currentChart === "income" ? "Income vs Forecast" : "Actual vs Budget"}
          </h2>
          <div style={{
            display: "flex",
            gap: "2px",
            marginBottom: " 24px",
            backgroundColor: "rgb(249, 250, 251)",
            padding: "4px",
            borderRadius: "12px"
          }}>
            <button
              style={currentChart === "expense" ? ActivebuttonStyle : buttonStyle}
              onClick={(e) => handleChartchange("expense")}
              onMouseEnter={(e) => {
                if (currentChart !== "expense") {
                  e.currentTarget.style.color = "#111827";
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
                }
              }}
              onMouseLeave={(e) => {
                if (currentChart !== "expense") {
                  e.currentTarget.style.color = "#6b7280";
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              Expense
            </button>
            <button
              style={currentChart === "income" ? ActivebuttonStyle : buttonStyle}
              onClick={(e) => handleChartchange("income")}
              onMouseEnter={(e) => {
                if (currentChart !== "income") {
                  e.currentTarget.style.color = "#111827";
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
                }
              }}
              onMouseLeave={(e) => {
                if (currentChart !== "income") {
                  e.currentTarget.style.color = "#6b7280";
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              Income
            </button>
          </div>
        </div>

        <Chart
        key={currentChart}   
          options={budgetChartOptions}
          series={budgetChartSeries}
          type="bar"
          height={320}
        />
      </div>
    </div>
  );
}

export default DashboardBudgetChart;